// @djsem/ui — Stem-Aware Set Builder & Transition model (Canonical Prompt 137).
//
// Pure, framework-free logic layering stem-lane visibility and stem-aware transition
// warnings onto the existing Set Builder (djoidSetBuilderModel.ts, prompt 94) and transition
// scoring (djoidGraphModel.ts). Built on the real stem contracts (DJSEMStemType from
// stemIntelligence.ts / stemAlignment.ts) — not the apps/poc "Victory Lap" demo types, which
// are a single hardcoded showcase, not a reusable primitive.
//
// Honesty note: this operates on stem *availability* (which approved stem types exist for a
// track), not on real-time energy-curve overlap — computing "these two vocal stems actually
// clash at this timestamp" needs bound runtime state (DJSEMStemRuntimeState) at a specific
// playback position, which a static set-planning lane view doesn't have. Warnings here name
// the risk and tell the DJ what to check, rather than fabricating precision DJSEM doesn't have.

/** A separable stem category, as surfaced to the UI. */
export type DJSEMStemTypeUI = "drums" | "bass" | "vocals" | "melody" | "other";

/** Canonical display order for stem lanes. */
export const STEM_LANE_ORDER: readonly DJSEMStemTypeUI[] = ["vocals", "drums", "bass", "melody", "other"];

/**
 * Human-readable label for a stem type.
 * @param stemType - The stem type to label.
 * @returns The display label.
 */
export function stemLaneLabel(stemType: DJSEMStemTypeUI): string {
  switch (stemType) {
    case "vocals": return "Vocals";
    case "drums": return "Drums";
    case "bass": return "Bass";
    case "melody": return "Melody";
    case "other": return "Other / FX";
  }
}

/** Only approved stems are shown by default (prompt 137 §5/Story 123.1). */
export interface TrackStemAvailability {
  trackId: string;
  /** Stem types with an approved (prompt 136) output for this track. */
  approvedStemTypes: DJSEMStemTypeUI[];
}

/** One lane row in the stem-aware set builder, showing per-track availability. */
export interface StemLaneRow {
  stemType: DJSEMStemTypeUI;
  label: string;
  /** trackId → available in this lane for that track. */
  availability: Record<string, boolean>;
}

/**
 * Build one row per stem lane, marking which tracks have that stem approved.
 * @param tracks - Tracks with their approved stem types.
 * @returns One {@link StemLaneRow} per lane, in {@link STEM_LANE_ORDER}.
 */
export function buildStemLanes(tracks: TrackStemAvailability[]): StemLaneRow[] {
  return STEM_LANE_ORDER.map((stemType) => ({
    stemType,
    label: stemLaneLabel(stemType),
    availability: Object.fromEntries(tracks.map((t) => [t.trackId, t.approvedStemTypes.includes(stemType)])),
  }));
}

/** A single stem-based transition warning for an adjacent track pair. */
export interface StemTransitionWarning {
  stemType: DJSEMStemTypeUI;
  message: string;
  suggestion: string;
}

/**
 * Stem-aware transition warnings for an adjacent pair (prompt 137 §6). Availability-based,
 * not overlap-timed (see file header) — tells the DJ what to check, doesn't claim to have
 * already checked it.
 * @param a - The outgoing track's stem availability.
 * @param b - The incoming track's stem availability.
 * @returns Warnings for each stem type both tracks share.
 */
export function stemAwareTransitionWarnings(a: TrackStemAvailability, b: TrackStemAvailability): StemTransitionWarning[] {
  const out: StemTransitionWarning[] = [];
  const both = (t: DJSEMStemTypeUI) => a.approvedStemTypes.includes(t) && b.approvedStemTypes.includes(t);
  if (both("vocals")) {
    out.push({ stemType: "vocals", message: "Both tracks have approved vocal stems.", suggestion: "Check for vocal overlap — plan a vocal-free mix window or an acapella tease." });
  }
  if (both("bass")) {
    out.push({ stemType: "bass", message: "Both tracks have approved bass stems.", suggestion: "Plan a bass-safe mix window — avoid stacking two dense low-ends." });
  }
  if (both("drums")) {
    out.push({ stemType: "drums", message: "Both tracks have approved drum stems.", suggestion: "A drum-only blend may give a smoother, continuity-driven transition." });
  }
  return out;
}

/**
 * Whole-set roll-up: how many adjacent pairs in this track order carry a stem-aware warning.
 * @param tracks - Ordered tracks with stem availability.
 * @returns The count of warned pairs and the total warning count across the set.
 */
export function stemAwareTransitionSummary(tracks: TrackStemAvailability[]): { pairsWithWarnings: number; totalWarnings: number } {
  let pairsWithWarnings = 0;
  let totalWarnings = 0;
  for (let i = 0; i < tracks.length - 1; i++) {
    const warnings = stemAwareTransitionWarnings(tracks[i], tracks[i + 1]);
    if (warnings.length > 0) { pairsWithWarnings++; totalWarnings += warnings.length; }
  }
  return { pairsWithWarnings, totalWarnings };
}
